import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { login, register } from '../services/api';
import { useAuthStore } from '../store/authStore';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Login: React.FC = () => {
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [showTcUpdate, setShowTcUpdate] = useState(false);
  const [pendingTcVersion, setPendingTcVersion] = useState('');
  const [loginToken, setLoginToken] = useState('');
  const [tcUpdateAccepted, setTcUpdateAccepted] = useState(false);
  const [acceptedTc, setAcceptedTc] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedHealth, setAcceptedHealth] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const { setAuth, setAnonim } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const prefillEmail = (location.state as any)?.email ?? '';

  // Pre-completează emailul dacă vine din /access
  React.useEffect(() => {
    if (prefillEmail) setEmail(prefillEmail);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async () => {
    setError('');
    if (!email || !password) { setError('Completează toate câmpurile!'); return; }
    setLoading(true);
    try {
      const res = await login(email, password);
      setAuth({ id: '', email }, res.data.access_token);
      if (res.data.requires_tc_update) {
        setLoginToken(res.data.access_token);
        setPendingTcVersion(res.data.current_tc_version);
        setShowTcUpdate(true);
      } else {
        navigate('/app');
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Email sau parolă incorecte!');
    } finally {
      setLoading(false);
    }
  };

  const handleTcAccept = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/profil/accept-tc`,
        { tc_version: pendingTcVersion },
        { headers: { Authorization: `Bearer ${loginToken}` } }
      );
      navigate('/app');
    } catch (e: any) {
      setError('Eroare la actualizarea termenilor. Încearcă din nou.');
      setShowTcUpdate(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTcLogout = async () => {
    try {
      await axios.post(
        `${API_URL}/auth/logout`, {},
        { headers: { Authorization: `Bearer ${loginToken}` } }
      );
    } catch {}
    setAuth({ id: '', email: '' }, '');
    setShowTcUpdate(false);
    setLoginToken('');
    setPendingTcVersion('');
    setTcUpdateAccepted(false);
  };

  const handleRegisterClick = () => {
    setError('');
    if (!email || !password) { setError('Completează toate câmpurile!'); return; }
    if (password !== password2) { setError('Parolele nu coincid!'); return; }
    if (password.length < 6) { setError('Parolă prea scurtă!'); return; }
    setShowPopup(true);
  };

  const handleRegisterConfirm = async () => {
    if (!acceptedTc || !acceptedPrivacy || !acceptedHealth) return;
    setLoading(true);
    try {
      await register(email, password, acceptedTc, acceptedPrivacy, acceptedHealth, acceptedMarketing);
      setShowPopup(false);
      setSuccess('Cont creat! Autentifică-te acum.');
      setTab('login');
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Eroare la înregistrare!');
      setShowPopup(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAnonim = () => {
    setAnonim();
    navigate('/chat-anonim');
  };

  const inputStyle: React.CSSProperties = {
    padding: '0.75rem', borderRadius: '10px',
    border: '1.5px solid #2a2a4a', background: '#12121f',
    color: '#e0e0f0', fontSize: '1rem', width: '100%',
    boxSizing: 'border-box'
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#12121f',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'DM Sans, sans-serif', padding: '1rem'
    }}>
      <div style={{
        background: '#1a1a30', borderRadius: '16px', padding: '2.5rem',
        width: '100%', maxWidth: '420px', border: '1px solid #2a2a4a'
      }}>
        <h1 style={{ color: '#fff', marginBottom: '0.5rem' }}>🩺 Flexara</h1>
        <p style={{ color: '#8080a0', marginBottom: '2rem' }}>
          Asistentul tău pentru sănătatea coloanei
        </p>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
          <button onClick={() => setTab('login')} style={{
            flex: 1, padding: '0.6rem', borderRadius: '8px', border: 'none',
            cursor: 'pointer', background: tab === 'login' ? '#4a4ad8' : '#2a2a4a',
            color: '#fff', fontWeight: 600
          }}>Autentificare</button>
          <button onClick={() => setTab('register')} style={{
            flex: 1, padding: '0.6rem', borderRadius: '8px', border: 'none',
            cursor: 'pointer', background: tab === 'register' ? '#4a4ad8' : '#2a2a4a',
            color: '#fff', fontWeight: 600
          }}>Cont nou</button>
        </div>

        {error && <p style={{ color: '#ff6060', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}
        {success && <p style={{ color: '#60d090', marginBottom: '1rem', fontSize: '0.9rem' }}>{success}</p>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <input type="email" placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
          <input type="password" placeholder="Parolă" value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && tab === 'login' && handleLogin()}
            style={inputStyle} />
          {tab === 'register' && (
            <input type="password" placeholder="Confirmă parola" value={password2}
              onChange={(e) => setPassword2(e.target.value)} style={inputStyle} />
          )}
          <button
            onClick={tab === 'login' ? handleLogin : handleRegisterClick}
            disabled={loading} style={{
              padding: '0.75rem', borderRadius: '10px', border: 'none',
              background: '#4a4ad8', color: '#fff', fontSize: '1rem',
              fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}>
            {loading ? 'Se încarcă...' : tab === 'login' ? 'Autentificare' : 'Continuă'}
          </button>
          {tab === 'login' && (
            <p style={{ textAlign: 'center', margin: 0 }}>
              <span onClick={() => navigate('/reset-parola-cerere')} style={{
                color: '#6060a0', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline'
              }}>
                Ai uitat parola?
              </span>
            </p>
          )}
        </div>

        <div style={{ marginTop: '1.5rem', borderTop: '1px solid #2a2a4a', paddingTop: '1.5rem' }}>
          <p style={{ color: '#6060a0', fontSize: '0.85rem', marginBottom: '0.75rem', textAlign: 'center' }}>
            Vrei să încerci fără cont?
          </p>
          <button onClick={handleAnonim} style={{
            width: '100%', padding: '0.65rem', borderRadius: '10px',
            border: '1px solid #2a2a4a', background: 'transparent',
            color: '#a0a0c0', cursor: 'pointer', fontSize: '0.9rem'
          }}>
            Continuă fără cont →
          </button>
        </div>

        <p style={{ color: '#6060a0', fontSize: '0.72rem', marginTop: '1.5rem', textAlign: 'center' }}>
          Informațiile oferite au scop educativ și nu înlocuiesc consultul medical.
        </p>
      </div>

      {/* Modal re-acceptare T&C la versiune nouă — non-dismissible */}
      {showTcUpdate && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: '#1a1a30', borderRadius: '16px', padding: '2rem',
            maxWidth: '480px', width: '100%', border: '1px solid #2a2a4a'
          }}>
            <h2 style={{ color: '#fff', marginBottom: '0.5rem' }}>📋 Termeni și Condiții actualizați</h2>
            <p style={{ color: '#8080a0', fontSize: '0.85rem', marginBottom: '1rem' }}>
              Am actualizat Termenii și Condițiile Flexara. Trebuie să accepți noua versiune pentru a continua să folosești aplicația.
            </p>
            <div style={{ background: '#1a1a3a', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', borderLeft: '3px solid #4a4ad8' }}>
              <p style={{ color: '#a0a0d0', fontSize: '0.82rem', margin: '0 0 0.4rem', fontWeight: 600 }}>Modificări în versiunea 1.2 (12.06.2026):</p>
              <ul style={{ color: '#a0a0d0', fontSize: '0.82rem', margin: 0, paddingLeft: '1.25rem' }}>
                <li>Adăugat Mailgun (Sinch Email) ca procesator de date pentru email tranzacțional</li>
                <li>Adăugată secțiunea privind lista de așteptare (waitlist)</li>
              </ul>
            </div>
            <div style={{ background: '#12121f', borderRadius: '8px', padding: '0.75rem', marginBottom: '1.5rem' }}>
              <p style={{ color: '#c0c0e0', fontSize: '0.85rem', margin: 0 }}>
                Citește noua versiune a{' '}
                <span onClick={() => window.open('/termeni', '_blank')}
                  style={{ color: '#4a4ad8', cursor: 'pointer', textDecoration: 'underline' }}>
                  Termenilor și Condițiilor
                </span>
                {' '}înainte de a accepta.
              </p>
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', marginBottom: '1.5rem' }}>
              <input type="checkbox" checked={tcUpdateAccepted}
                onChange={e => setTcUpdateAccepted(e.target.checked)}
                style={{ marginTop: '3px', accentColor: '#4a4ad8', width: '16px', height: '16px', flexShrink: 0 }} />
              <span style={{ color: '#c0c0e0', fontSize: '0.9rem' }}>
                Am citit și accept noua versiune a Termenilor și Condițiilor Flexara. *(obligatoriu)
              </span>
            </label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button onClick={handleTcLogout} style={{
                flex: 1, padding: '0.75rem', borderRadius: '10px',
                border: '1px solid #2a2a4a', background: 'transparent',
                color: '#a0a0c0', cursor: 'pointer'
              }}>Deconectează-mă</button>
              <button onClick={handleTcAccept}
                disabled={!tcUpdateAccepted || loading}
                style={{
                  flex: 2, padding: '0.75rem', borderRadius: '10px', border: 'none',
                  background: tcUpdateAccepted && !loading ? '#4a4ad8' : '#2a2a4a',
                  color: tcUpdateAccepted && !loading ? '#fff' : '#6060a0',
                  cursor: tcUpdateAccepted && !loading ? 'pointer' : 'not-allowed',
                  fontWeight: 600
                }}>
                {loading ? 'Se salvează...' : 'Accept și continuă'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup acceptare T&C */}
      {showPopup && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: '#1a1a30', borderRadius: '16px', padding: '2rem',
            maxWidth: '480px', width: '100%', border: '1px solid #2a2a4a'
          }}>
            <h2 style={{ color: '#fff', marginBottom: '0.5rem' }}>📋 Termeni și Condiții</h2>
            <p style={{ color: '#8080a0', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              Pentru a crea un cont, trebuie să accepți documentele de mai jos.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={acceptedTc}
                  onChange={e => setAcceptedTc(e.target.checked)}
                  style={{ marginTop: '3px', accentColor: '#4a4ad8', width: '16px', height: '16px', flexShrink: 0 }} />
                <span style={{ color: '#c0c0e0', fontSize: '0.9rem' }}>
                  Am citit și accept{' '}
                  <span onClick={() => window.open('/termeni', '_blank')}
                    style={{ color: '#4a4ad8', cursor: 'pointer', textDecoration: 'underline' }}>
                    Termenii și Condițiile
                  </span>
                  {' '}*(obligatoriu)
                </span>
              </label>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={acceptedPrivacy}
                  onChange={e => setAcceptedPrivacy(e.target.checked)}
                  style={{ marginTop: '3px', accentColor: '#4a4ad8', width: '16px', height: '16px', flexShrink: 0 }} />
                <span style={{ color: '#c0c0e0', fontSize: '0.9rem' }}>
                  Am citit și accept{' '}
                  <span onClick={() => window.open('/confidentialitate', '_blank')}
                    style={{ color: '#4a4ad8', cursor: 'pointer', textDecoration: 'underline' }}>
                    Politica de Confidențialitate
                  </span>
                  {' '}*(obligatoriu)
                </span>
              </label>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={acceptedHealth}
                  onChange={e => setAcceptedHealth(e.target.checked)}
                  style={{ marginTop: '3px', accentColor: '#4a4ad8', width: '16px', height: '16px', flexShrink: 0 }} />
                <span style={{ color: '#c0c0e0', fontSize: '0.9rem' }}>
                  Îmi exprim consimțământul <strong>explicit</strong> pentru procesarea datelor mele de sănătate (nivel durere, simptome, istoricul conversațiilor) în scopul furnizării serviciului Flexara, conform{' '}
                  <span onClick={() => window.open('/confidentialitate', '_blank')}
                    style={{ color: '#4a4ad8', cursor: 'pointer', textDecoration: 'underline' }}>
                    Art. 9 GDPR
                  </span>
                  {' '}*(obligatoriu)
                </span>
              </label>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={acceptedMarketing}
                  onChange={e => setAcceptedMarketing(e.target.checked)}
                  style={{ marginTop: '3px', accentColor: '#4a4ad8', width: '16px', height: '16px', flexShrink: 0 }} />
                <span style={{ color: '#c0c0e0', fontSize: '0.9rem' }}>
                  Doresc să primesc noutăți, sfaturi de sănătate și oferte de la Flexara prin email. *(opțional)
                </span>
              </label>

              <div style={{ background: '#12121f', borderRadius: '8px', padding: '0.75rem' }}>
                <p style={{ color: '#6060a0', fontSize: '0.8rem', margin: 0 }}>
                  🍪 Folosim doar cookies tehnice strict necesare pentru funcționarea aplicației.{' '}
                  <span onClick={() => window.open('/cookies', '_blank')}
                    style={{ color: '#4a4ad8', cursor: 'pointer', textDecoration: 'underline' }}>
                    Politica Cookies
                  </span>
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button onClick={() => setShowPopup(false)} style={{
                flex: 1, padding: '0.75rem', borderRadius: '10px',
                border: '1px solid #2a2a4a', background: 'transparent',
                color: '#a0a0c0', cursor: 'pointer'
              }}>Anulează</button>
              <button onClick={handleRegisterConfirm}
                disabled={!acceptedTc || !acceptedPrivacy || !acceptedHealth || loading}
                style={{
                  flex: 2, padding: '0.75rem', borderRadius: '10px', border: 'none',
                  background: acceptedTc && acceptedPrivacy && acceptedHealth ? '#4a4ad8' : '#2a2a4a',
                  color: acceptedTc && acceptedPrivacy && acceptedHealth ? '#fff' : '#6060a0',
                  cursor: acceptedTc && acceptedPrivacy && acceptedHealth ? 'pointer' : 'not-allowed',
                  fontWeight: 600
                }}>
                {loading ? 'Se creează...' : 'Creează cont'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
